import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { AuthClient } from '@mrrmlab/auth';
import { HomeRoute } from './routes/home';
import { LoginRoute } from './routes/login';
import { ListsRoute } from './routes/lists';
import { TodoRoute } from './routes/todo';
import { EinkaufenRoute } from './routes/einkaufen';
import { HeuteRoute } from './routes/heute';
import { ListDetailRoute } from './routes/list-detail';
import { ListShoppingModeRoute } from './routes/list-shopping-mode';
import { ListImagePreviewRoute } from './routes/list-image-preview';
import { ListImageDebugRoute } from './routes/list-image-debug';
import { ListImageAnalyzeRoute } from './routes/list-image-analyze';
import { ListReviewRoute } from './routes/list-review';
import { ListTextImportRoute } from './routes/list-text-import';
import { SettingsLayout, EinstellungenIndexRoute } from './routes/einstellungen-layout';
import { EinstellungenKalenderRoute } from './routes/einstellungen-kalender';
import { EinstellungenHabitsRoute } from './routes/einstellungen-habits';
import { EinstellungenProjekteRoute } from './routes/einstellungen-projekte';
import { EinstellungenMailRoute } from './routes/einstellungen-mail';
import { EinstellungenTagsRoute } from './routes/einstellungen-tags';
import { EinstellungenGeschaefteRoute } from './routes/einstellungen-geschaefte';
import { StoreCatalogRoute } from './routes/store-catalog';
import { EinstellungenTrainingRoute } from './routes/einstellungen-training';
import { KontoRoute } from './routes/konto';
import { MailRoute } from './routes/mail';
import { PaketeRoute } from './routes/pakete';
import { PaketeDetailRoute } from './routes/pakete-detail';
import { MailNewAccountRoute } from './routes/mail-new-account';
import { MailFoldersRoute } from './routes/mail-folders';
import { MailMessagesRoute } from './routes/mail-messages';
import { MailReaderRoute } from './routes/mail-reader';
import { MailReviewRoute } from './routes/mail-review';
import { ArchivRoute, type ArchivSearch } from './routes/archiv';
import { ArchivDetailRoute } from './routes/archiv-detail';
import { AuthCallbackRoute } from './routes/auth-callback';
import { RoadmapRoute } from './routes/roadmap';
import { Layout } from './routes/layout';

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthClient | null;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search['returnTo'] === 'string' ? search['returnTo'] : undefined,
  }),
  component: function LoginPage() {
    const { returnTo } = loginRoute.useSearch();
    return <LoginRoute returnTo={returnTo} />;
  },
});

const listsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListsRoute,
});

const todoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/todo',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: TodoRoute,
});

const einkaufenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einkaufen',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: EinkaufenRoute,
});

const heuteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/heute',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: HeuteRoute,
});

const listDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListDetailRoute,
});

const listShoppingModeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/einkaufen',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListShoppingModeRoute,
});

const listImagePreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/image-preview',
  validateSearch: (search: Record<string, unknown>) => ({
    model: typeof search['model'] === 'string' ? search['model'] : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListImagePreviewRoute,
});

const listImageDebugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/image-debug',
  validateSearch: (search: Record<string, unknown>) => ({
    model: typeof search['model'] === 'string' ? search['model'] : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListImageDebugRoute,
});

const listImageAnalyzeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/image-analyze',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListImageAnalyzeRoute,
});

const listReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/review',
  validateSearch: (search: Record<string, unknown>) => ({
    items: typeof search['items'] === 'string' ? search['items'] : '[]',
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListReviewRoute,
});

const listTextImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lists/$listId/text-import',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ListTextImportRoute,
});

const storesRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stores',
  beforeLoad: () => {
    throw redirect({ to: '/einstellungen/geschaefte' });
  },
  component: () => null,
});

const einstellungenLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einstellungen',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: SettingsLayout,
});

const einstellungenIndexRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: '/',
  component: EinstellungenIndexRoute,
});

const einstellungenMailRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'mail',
  component: EinstellungenMailRoute,
});

const einstellungenTagsRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'tags',
  component: EinstellungenTagsRoute,
});

const einstellungenGeschaefteRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'geschaefte',
  component: EinstellungenGeschaefteRoute,
});

const storeCatalogRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'geschaefte/$storeId/catalog',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: StoreCatalogRoute,
});

const einstellungenTrainingRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'training',
  component: EinstellungenTrainingRoute,
});

const einstellungenKalenderRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'kalender',
  component: EinstellungenKalenderRoute,
});

const einstellungenHabitsRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'habits',
  component: EinstellungenHabitsRoute,
});

const einstellungenProjekteRoute = createRoute({
  getParentRoute: () => einstellungenLayoutRoute,
  path: 'projekte',
  component: EinstellungenProjekteRoute,
});

const kontoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/konto',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: KontoRoute,
});

const mailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailRoute,
});

const paketeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pakete',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: PaketeRoute,
});

const paketeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pakete/$trackingId',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: function PaketeDetailPage() {
    const { trackingId } = paketeDetailRoute.useParams();
    return <PaketeDetailRoute trackingId={trackingId} />;
  },
});

const mailNewAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/new-account',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailNewAccountRoute,
});

const mailFoldersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/$accountId/folders',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailFoldersRoute,
});

const mailMessagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/$accountId/messages',
  validateSearch: (search: Record<string, unknown>): { folder: string } => ({
    folder: typeof search['folder'] === 'string' ? search['folder'] : 'INBOX',
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailMessagesRoute,
});

const mailReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/review',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailReviewRoute,
});

const mailTagsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/tags',
  beforeLoad: () => {
    throw redirect({ to: '/einstellungen/tags' });
  },
  component: () => null,
});

const mailReaderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/$accountId/reader',
  validateSearch: (search: Record<string, unknown>): { folder: string; uid: number } => ({
    folder: typeof search['folder'] === 'string' ? search['folder'] : 'INBOX',
    uid: typeof search['uid'] === 'number' ? search['uid'] : Number(search['uid']),
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: MailReaderRoute,
});

const archivRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/archiv',
  validateSearch: (search: Record<string, unknown>): ArchivSearch => ({
    query: typeof search['query'] === 'string' ? search['query'] : undefined,
    tags: typeof search['tags'] === 'string' ? search['tags'] : undefined,
    correspondent: typeof search['correspondent'] === 'number' ? search['correspondent'] : undefined,
    documentType: typeof search['documentType'] === 'number' ? search['documentType'] : undefined,
    page: typeof search['page'] === 'number' ? search['page'] : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ArchivRoute,
});

const archivDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/archiv/$documentId',
  beforeLoad: async ({ context, location }) => {
    await requireAuth(context.auth, location.href);
  },
  component: ArchivDetailRoute,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackRoute,
});

const roadmapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/roadmap',
  component: RoadmapRoute,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  loginRoute,
  listsRoute,
  todoRoute,
  einkaufenRoute,
  heuteRoute,
  listDetailRoute,
  listShoppingModeRoute,
  listImagePreviewRoute,
  listImageDebugRoute,
  listImageAnalyzeRoute,
  listReviewRoute,
  listTextImportRoute,
  storesRedirectRoute,
  einstellungenLayoutRoute.addChildren([
    einstellungenIndexRoute,
    einstellungenMailRoute,
    einstellungenTagsRoute,
    einstellungenGeschaefteRoute,
    storeCatalogRoute,
    einstellungenTrainingRoute,
    einstellungenKalenderRoute,
    einstellungenHabitsRoute,
    einstellungenProjekteRoute,
  ]),
  kontoRoute,
  mailRoute,
  paketeRoute,
  paketeDetailRoute,
  mailReviewRoute,
  mailTagsRedirectRoute,
  mailNewAccountRoute,
  mailFoldersRoute,
  mailMessagesRoute,
  mailReaderRoute,
  archivRoute,
  archivDetailRoute,
  authCallbackRoute,
  roadmapRoute,
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

async function requireAuth(auth: AuthClient | null, returnTo: string): Promise<void> {
  if (!auth) return;
  if (await auth.isAuthenticated()) return;
  throw redirect({ to: '/login', search: { returnTo } });
}
